/**
 * **最近用过的节点类型** —— 添加菜单顶部那一组的来源。
 *
 * ## 为什么自动记，而不是让用户手动收藏
 *
 * Blender 的做法是 Quick Favorites：右键菜单项 → 加入收藏。那需要用户
 * **主动管理**一份清单 —— 而搭模板时真正的问题是「我今天已经加了 8 次
 * 常量 · 数值」，用户不会为此去建收藏。
 *
 * 自动记更简单、也更快见效：不用维护、不用教、第一次用过就有了。
 * 代价是「想固定某几个」做不到 —— 那用搜索（它永远在）。
 *
 * ## 只记 id，不记定义
 *
 * 节点类型由**包**提供、可以装卸。所以这里只存类型标识，
 * 渲染时再去注册表查一遍 —— 查不到（包卸载了）就默默不显示。
 * 存整份定义的话，包升级之后菜单里会出现一份**旧的**节点。
 */
import { computed, ref } from 'vue'
import { nodeTypeOf } from '@/playground/nodeTypes'

const STORAGE_KEY = 'node-print.recent-nodes.v1'

/**
 * 记几个。
 *
 * ⚠️ 6 个是有意的：菜单顶部那一组是**快捷方式**，长了它就变成
 * 「第二份完整目录」，反而更难扫。Blender 的 Quick Favorites 也建议少放。
 */
const MAX_RECENT = 6

function read(): string[] {
  if (typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    /* 存坏了就当没有过 —— 一份快捷清单不值得让编辑器报错 */
    return []
  }
}

function write(types: readonly string[]): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(types))
  } catch {
    /* 配额满 / 隐私模式：记不住最近用过的不该报错 */
  }
}

export function useRecentNodes() {
  const types = ref<string[]>(read())

  /**
   * 记一次使用。
   *
   * ⚠️ 用「先删再插到队首」而不是「push + 截断」：
   * 重复用一个节点时，它必须**移到最前**，而不是原地不动 ——
   * 否则「最近」会变成「最早用过的几个」，那就没用了。
   */
  function remember(type: string): void {
    const next = [type, ...types.value.filter((t) => t !== type)].slice(0, MAX_RECENT)
    if (next.length === types.value.length && next.every((t, i) => t === types.value[i])) return
    types.value = next
    write(next)
  }

  /**
   * 能在菜单里显示的（**还在目录里**的）。
   *
   * ⚠️ 这里必须查注册表：包可以卸载，存下来的类型可能已经不存在了。
   * 查不到就过滤掉，而不是显示一个点了没反应的项。
   */
  const available = computed(() => types.value.filter((type) => nodeTypeOf(type) !== undefined))

  function clear(): void {
    types.value = []
    write([])
  }

  return { recentTypes: available, remember, clear }
}
